//@flow
import React, { Component } from "react";
import { Grid, Typography, withStyles } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Cancel";
import CheckIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import classNames from "classnames";

import { ConfirmCancelDialog } from "imagus-components";

import {
  FaceDetectionResultType,
  EyeType
} from "../../../global/types/FaceType";
import { roundFloat } from "../../../global/functions/StringOperations";

const styles = (theme: DictionaryType): ImageQualityClassesType => ({
  title: {
    fontWeight: 600,
    width: "auto"
  },
  leftMargin: {
    marginLeft: "10px"
  },

  approveIcon: {
    color: "#039487"
    // backgroundColor: "#039487",
  },

  denyIcon: {
    color: "#FF0000"
  },
  warningIcon: {
    color: theme.palette.warning.main
  },
  textWithIcon: {
    marginLeft: "5px",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap"
  }
});

type ImageQualityClassesType = {
  title: DictionaryType,
  leftMargin: DictionaryType,
  approveIcon: DictionaryType,
  denyIcon: DictionaryType,
  textWithIcon: DictionaryType
};

interface ImageQualityDetailsProps {
  classes: ImageQualityClassesType;
  faces: FaceDetectionResultType[];
  index: number;
  setImageErrors: (message: string) => void;
  personUid?: string;
  currentEnrolmentLength: number;
  eyes: EyeType;
  syncQualityError: (isError: boolean) => void;
}

class ImageQualityDetails extends Component<ImageQualityDetailsProps, *> {
  constructor(props: ImageQualityDetailsProps) {
    super(props);
    this.state = {
      blurry: false,
      confidence: false,
      frontal: false,
      person: false
    };
  }

  componentDidMount() {
    if (
      this.props.index !== null &&
      this.props.index < this.props.faces.length
    ) {
      //do quality check if detected a face, and not an added face
      let face = this.props.faces[this.props.index];
      this.setState(
        {
          blurry: this.passBluriness(face),
          confidence: this.passConfidenceThreshold(face),
          frontal: this.passFrontalnessThreshold(face),
          person: this.passPersonCheck(face)
        },
        () => {
          this.props.syncQualityError(
            Object.values(this.state).includes(false)
          );
        }
      );
    }
  }
  componentDidUpdate(prevProps: ImageQualityDetailsProps) {
    if (
      this.props.index !== null &&
      this.props.index < this.props.faces.length
    ) {
      //do quality check if detected a face, and not an added face
      if (
        prevProps.faces !== this.props.faces ||
        prevProps.index !== this.props.index
      ) {
        //only run when faces and index is different
        let face = this.props.faces[this.props.index];
        this.setState(
          {
            blurry: this.passBluriness(face),
            confidence: this.passConfidenceThreshold(face),
            frontal: this.passFrontalnessThreshold(face),
            person: this.passPersonCheck(face)
          },
          () => {
            this.props.syncQualityError(
              Object.values(this.state).includes(false)
            );
          }
        );
      }
    }
  }

  passBluriness = (face: FaceDetectionResultType): boolean => {
    let result = face.resultMetrics.matchBlurrinessThreshold > 1;
    if (result) return result;
  };

  passConfidenceThreshold = (face: FaceDetectionResultType): boolean => {
    let result = face.resultMetrics.matchConfidenceThreshold > 1;
    return result;
  };

  passFrontalnessThreshold = (face: FaceDetectionResultType): boolean => {
    let result = face.resultMetrics.matchFrontalThreshold > 1;
    return result;
  };

  passPersonCheck = (face: FaceDetectionResultType): boolean => {
    const exist = face.search && face.search.probability > 0;

    if (exist) {
      //current person NOT match with search result
      if (face.search.profileId !== this.props.personUid) {
        return false;
      }
    } else {
      //current person has photo and photo has NO match result
      if (this.props.personUid && this.props.currentEnrolmentLength > 0) {
        return false;
      }
    }

    return true;
  };

  render() {
    const classes = this.props.classes;

    const DetailItem = ({
      title,
      content
    }: {
      title: string,
      content: JSX.Element | string
    }): React.Component => {
      return (
        <Grid item xs={12}>
          <Typography variant="subtitle1" display="inline">
            <span className={classes.title}>{title}</span>

            {content}
          </Typography>
        </Grid>
      );
    };

    const passThreshold = (
      <span className={classes.textWithIcon}>
        <CheckIcon className={classes.approveIcon} fontSize="large" />
        Yes!
      </span>
    );

    const failThreshold = (message: string) => {
      return (
        <span className={classes.textWithIcon}>
          <CloseIcon className={classes.denyIcon} fontSize="large" />
          {message}
        </span>
      );
    };

    const NoDetails = ({ message }: { message: string }): React.Component => {
      return (
        <span className={classes.textWithIcon}>
          <WarningIcon className={classes.warningIcon} fontSize="large" />
          {message}
        </span>
      );
    };

    let selectedFace = this.props.faces[this.props.index];

    return (
      <Grid
        item
        xs={12}
        md={5}
        container
        spacing={8}
        style={{ padding: "20px" }}
      >
        <DetailItem
          title="Number of faces detected: "
          content={this.props.faces.length.toString()}
        />
        {this.props.index !== null ? (
          /** Display enrolment details */
          this.props.index < this.props.faces.length ? (
            <>
              {/* <DetailItem
              title="Can see a face?"
              content={
                selectedFace.faceness === 1.0
                  ? passThreshold
                  : failThreshold("This may not be a face")
              }
            /> */}
              <Grid item />
              <DetailItem
                title="Is photo clear"
                content={
                  this.state.blurry
                    ? passThreshold
                    : failThreshold("Face is too blurry")
                }
              />

              <DetailItem
                title="Confidence"
                content={
                  this.state.confidence
                    ? passThreshold
                    : failThreshold("Bad contrast or bad lighting")
                }
              />

              <DetailItem
                title="Frontal"
                content={
                  this.state.frontal
                    ? passThreshold
                    : failThreshold("Face is NOT looking forward")
                }
              />

              <DetailItem
                title="Face belong to person?"
                content={
                  this.state.person
                    ? passThreshold
                    : failThreshold("The photo may not belong to this person")
                }
              />
            </>
          ) : (
            <NoDetails message="No quality details available" />
          )
        ) : (
          <NoDetails message="No face selected" />
        )}
      </Grid>
    );
  }
}

export default withStyles(styles)(ImageQualityDetails);
